import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, SearchInput } from 'app/components/ui';

type TypographyVariant = NonNullable<ComponentProps<typeof Typography>['variant']>;

interface Stats {
  total: number;
  complete: number;
  partial: number;
  missing: number;
  tablesTotal: number;
  tablesComplete: number;
}

type RowFilter = 'all' | 'complete' | 'partial' | 'missing';

interface Props {
  stats: Stats;
  search: string;
  setSearch: (s: string) => void;
  filter: RowFilter;
  setFilter: (f: RowFilter) => void;
  onClose: () => void;
}

const Card = ({ label, value, tone }: { label: string; value: number; tone: string }) => {
  const valueVariant: string = 'heading-3';
  return (
    <div className={`coverage-overview-card coverage-overview-card--${tone}`}>
      <div className="coverage-overview-card__value">
        <Typography variant={valueVariant as TypographyVariant} fontWeight="bold">
          {value}
        </Typography>
      </div>
      <div className="coverage-overview-card__label">
        <Typography variant="body-5">{label}</Typography>
      </div>
    </div>
  );
};

export const CoverageOverviewHeader = ({ stats, search, setSearch, filter, setFilter, onClose }: Props) => {
  const { t } = useTranslation(['common', 'tag']);

  return (
    <div className="coverage-overview-header">
      <div className="coverage-overview-header__cards">
        <Card label={t('tag:coverage.stats.totalRegions')} value={stats.total} tone="total" />
        <Card label={t('tag:coverage.stats.complete')} value={stats.complete} tone="complete" />
        <Card label={t('tag:coverage.stats.partial')} value={stats.partial} tone="partial" />
        <Card label={t('tag:coverage.stats.missing')} value={stats.missing} tone="missing" />
        <Card label={t('tag:coverage.stats.tablesMarked')} value={stats.tablesTotal} tone="total" />
        <Card label={t('tag:coverage.stats.tablesComplete')} value={stats.tablesComplete} tone="complete" />
      </div>
      <div className="coverage-overview-header__controls">
        <div className="coverage-overview-header__filters">
          {(['all', 'complete', 'partial', 'missing'] as RowFilter[]).map((f) => (
            <button key={f} type="button" className={`coverage-overview-header__filter${filter === f ? ' is-active' : ''}`} onClick={() => setFilter(f)}>
              {t(`tag:coverage.filter.${f}`)}
            </button>
          ))}
        </div>
        <SearchInput searchInput={search} setSearchInput={setSearch} placeholder={t('tag:coverage.search.placeholder')} />
        <button type="button" className="coverage-overview-header__close" onClick={onClose} aria-label={t('common:close')}>
          <i className="fa-regular fa-xmark" />
        </button>
      </div>
    </div>
  );
};
